import { realpathSync } from "node:fs";
import { isAbsolute, relative, sep } from "node:path";
import { normalizePath, type PathInputOptions, resolvePath } from "@caupulican/pi-agent-core/paths";
import { spawnProcessSync } from "./child-process.ts";

// normalizePath/resolvePath (and PathInputOptions) are promoted into the kernel's pure paths module.
// Re-export them so existing coding-agent consumers keep importing from this module unchanged.
export { normalizePath, type PathInputOptions, resolvePath };

/**
 * Resolve a path to its canonical (real) form, following symlinks.
 * Falls back to the raw path if resolution fails (e.g. the target does
 * not exist yet), so that callers never crash on missing filesystem
 * entries.
 */
export function canonicalizePath(path: string): string {
	try {
		return realpathSync.native(path);
	} catch {
		return path;
	}
}

/**
 * Returns true if the value is NOT a package source (npm:, git:, etc.)
 * or a remote URL protocol. Bare names, relative paths, and file: URLs
 * are considered local.
 */
export function isLocalPath(value: string): boolean {
	const trimmed = value.trim();
	// Known non-local prefixes. file: URLs are local paths and are intentionally resolved by resolvePath().
	if (
		trimmed.startsWith("npm:") ||
		trimmed.startsWith("git:") ||
		trimmed.startsWith("github:") ||
		trimmed.startsWith("http:") ||
		trimmed.startsWith("https:") ||
		trimmed.startsWith("ssh:")
	) {
		return false;
	}
	return true;
}

export function getCwdRelativePath(filePath: string, cwd: string): string | undefined {
	const resolvedCwd = resolvePath(cwd);
	const resolvedPath = resolvePath(filePath, resolvedCwd);
	const relativePath = relative(resolvedCwd, resolvedPath);
	const isInsideCwd =
		relativePath === "" ||
		(relativePath !== ".." && !relativePath.startsWith(`..${sep}`) && !isAbsolute(relativePath));

	return isInsideCwd ? relativePath || "." : undefined;
}

export function formatPathRelativeToCwdOrAbsolute(filePath: string, cwd: string): string {
	const absolutePath = resolvePath(filePath, cwd);
	const resolvedCwd = resolvePath(cwd);
	const absoluteDisplay = absolutePath.split(sep).join("/");
	const relativeDisplay = (relative(resolvedCwd, absolutePath) || ".").split(sep).join("/");
	return relativeDisplay.length < absoluteDisplay.length ? relativeDisplay : absoluteDisplay;
}

export function markPathIgnoredByCloudSync(path: string): void {
	const attrs =
		process.platform === "darwin"
			? ["com.dropbox.ignored", "com.apple.fileprovider.ignore#P"]
			: process.platform === "linux"
				? ["user.com.dropbox.ignored"]
				: [];

	for (const attr of attrs) {
		if (process.platform === "darwin") {
			spawnProcessSync("xattr", ["-w", attr, "1", path], {
				encoding: "utf-8",
				stdio: "ignore",
				timeout: 2_000,
			});
		} else {
			spawnProcessSync("setfattr", ["-n", attr, "-v", "1", path], {
				encoding: "utf-8",
				stdio: "ignore",
				timeout: 2_000,
			});
		}
	}
}
